// Lets a professional generate (via DataTalkPatientMessageSpecification, backed by
// datatalk/patient_message.py), review/edit, and send a short LLM-drafted message to the
// patient. Sending both delivers the message to the patient AND saves it as a
// professional note, so it shows up in the patient's chart even though it was authored
// as a message.
import { useState } from "react"
import {
    CreateProfessionalNoteSpecification,
    DataTalkPatientMessageSpecification,
    SendPatientMessageSpecification,
} from "pokal_api"
import { useAppSelector } from "../../../../redux/store/hooks"
import { selectDashboardSelectedPatient } from "../../../../redux/ProfessionalDashboard"

type Props = {
    open: boolean
    onClose: () => void
    patientId: string
    mode: "monitoring" | "prediction"
    selectedModel: string
    openaiApiKey?: string
}

export default function PatientMessageModal({
    open,
    onClose,
    patientId,
    mode,
    selectedModel,
    openaiApiKey,
}: Props) {
    const [composedMessage, setComposedMessage] = useState("")
    const [isGenerating, setIsGenerating] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [messageSent, setMessageSent] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const selectedPatient = useAppSelector(selectDashboardSelectedPatient)

    if (!open) return null

    const modeLabel = mode === "monitoring" ? "Monitoring" : "Vorhersage"

    async function generateMessage() {
        setIsGenerating(true)
        setComposedMessage("")
        setMessageSent(false)
        setError(null)
        try {
            const { message } = await DataTalkPatientMessageSpecification.request({
                patientId,
                mode,
                llm_model: selectedModel,
                openai_api_key: openaiApiKey,
            })
            if (message && message.trim()) {
                setComposedMessage(message)
            } else {
                setError("Es wurde kein Entwurf zurückgegeben. Bitte erneut versuchen.")
            }
        } catch {
            setError(
                "Entwurf konnte nicht erstellt werden. Das Sprachmodell hat möglicherweise zu lange gebraucht – bitte erneut versuchen.",
            )
        } finally {
            setIsGenerating(false)
        }
    }

    async function sendMessage() {
        if (!composedMessage.trim()) return
        setIsSending(true)
        try {
            await Promise.all([
                SendPatientMessageSpecification.request({ patientId, content: composedMessage }),
                CreateProfessionalNoteSpecification.request({
                    patient: selectedPatient?.id ?? patientId,
                    content: composedMessage,
                }),
            ])
            setComposedMessage("")
            setMessageSent(true)
            setTimeout(() => setMessageSent(false), 3000)
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
            onClick={e => { if (e.target === e.currentTarget) onClose() }}
        >
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-800">
                        ✉ Nachricht an Patient:in
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                        aria-label="Schließen"
                    >
                        ✕
                    </button>
                </div>

                <div className="px-5 py-4 space-y-4">
                    <p className="text-xs text-gray-500 leading-relaxed">
                        KI-generierter Nachrichtenentwurf basierend auf den aktuellen{" "}
                        <span className="font-medium">{modeLabel}</span>-Daten. Vor dem Senden
                        prüfen und anpassen.
                    </p>

                    <textarea
                        value={composedMessage}
                        onChange={e => setComposedMessage(e.target.value)}
                        rows={5}
                        placeholder="Nachricht verfassen oder KI-Entwurf generieren…"
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"
                    />

                    <button
                        type="button"
                        onClick={generateMessage}
                        disabled={isGenerating}
                        className="w-full rounded-lg border border-teal-300 bg-white px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50 transition-colors"
                    >
                        {isGenerating ? "Wird generiert… (kann einen Moment dauern)" : "KI-Entwurf generieren"}
                    </button>

                    {error && (
                        <p className="text-xs text-red-600 leading-relaxed">{error}</p>
                    )}
                </div>

                <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <button
                        type="button"
                        onClick={sendMessage}
                        disabled={isSending || !composedMessage.trim()}
                        className="rounded-lg bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50 transition-colors"
                    >
                        {isSending ? "Wird gesendet…" : "An Patient:in senden"}
                    </button>
                    {messageSent && (
                        <span className="text-xs text-teal-600 font-medium">✓ Gesendet</span>
                    )}
                </div>
            </div>
        </div>
    )
}
